import { useCallback, useMemo } from "react";
import { TextIconButton, CirclePlusIcon, SearchInput, RowActions } from "shared-portal-component";
import type { CellContext, ColumnDef } from "@tanstack/react-table";

import TwoRowLayout from '@/layout/TwoRowLayout/TwoRowLayout';
import TwoColumn from '@/components/atomic/TwoColumn/TwoColumn';
import '@/components/atomic/Table/Table.scss';
import type { SixOwner } from "@/types/six/Six";
import CreateSixModal from "@/components/organism/CreateSix/CreateSixModal";
import Table from "@/components/atomic/Table/Table";
import ConnectionStatusDot from "@/components/atomic/ConnectionStatusDot/ConnectionStatusDot";
import { formatDateTime } from "@/utils/format/date.utils";
import { useSixCatalog, type SixWithConnection } from "@/hooks/six/useSixCatalog";
import { useAppDispatch } from "@/store";
import { showEditSixModal } from "@/store/thunks/ui/uiThunk";
import { fetchParticipants } from "@/store/thunks/sixDetail/fetchParticipantsThunk";
import { openConfirm } from "@/store/slices/ui/uiSlice";
import { getUserTypeFromToken } from "@/store/utils/jwtDecoder";
import { getSessionToken } from "@/utils/sessionStorage";

const SixRowActions: React.FC<{ six: SixWithConnection }> = ({ six }) => {
  const dispatch = useAppDispatch();

  const handleEditClick = useCallback(async() => {
    await dispatch(fetchParticipants(six.id));
    dispatch(showEditSixModal(six));
  }, [dispatch, six]);

  const handleDeleteClick = useCallback(() => {
    dispatch(
      openConfirm({
        context: 'sixes',
        kind: 'delete',
        row: { id: six.id, name: six.name },
        payload: { id: six.id },
      })
    );
  }, [dispatch, six]);

  const actions = useMemo(
    () => [
      { label: 'Edit Six', onClick: handleEditClick },
      { label: 'Delete', onClick: handleDeleteClick },
    ],
    [handleEditClick, handleDeleteClick]
  );

  return (
    <div onClick={(event) => { event.stopPropagation(); }}>
      <RowActions actions={actions} />
    </div>
  );
};

const columns: Array<ColumnDef<SixWithConnection>> = [
  {
    accessorKey: 'name',
    header: 'SIX Name',
    cell: (info: CellContext<SixWithConnection, unknown>)  => {
      const status = info.row.original.connectionStatus;

      return (
      <span className="six-name-cell">
        <ConnectionStatusDot
          isConnected={status.isConnected}
          relayCount={status.relayCount}
          peerCount={status.peerCount}
        />
        <img src="/tableIcon.svg" className="six-icon" alt="" />
        <span className="six-name">{info.getValue<string>()}</span>
      </span>
    )},
  },
  {
    accessorKey: 'owners',
    header: 'SIX owner(s)',
    cell: (info: CellContext<SixWithConnection, unknown>) => {
      const owners = info.getValue<SixOwner[]>();
      return (
        <span className="six-owners">
          {owners.map((o: SixOwner) => o.name).join(', ')}
        </span>
      );
    },
  },
  {
    accessorKey: 'lastModified',
    header: 'Last Modified',
    cell: (info: CellContext<SixWithConnection, unknown>) => {
      const lastModified = info.getValue<string | number | Date>();
      return (
        <span className="six-lastmodified">
          {formatDateTime(lastModified)}
        </span>
      );
    },
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: info =>
      <span className="six-description">{info.getValue<string>()}</span>,
  },
  {
    id: 'actions',
    header: '',
    cell: (info) => <SixRowActions six={info.row.original} />,
  },
];

export function SixListing() {
  const { state, handlers } = useSixCatalog();
  const token = getSessionToken();
  const userType = getUserTypeFromToken(token ?? '');
  const isExternalUser = userType === 'External';

  const handleRowClick = (six: SixWithConnection) => {
    handlers.navigateToSix(six.id);
  };

const getNewSixButton = () => {
  if (isExternalUser) return <></>;
return (
<TextIconButton
                icon={<CirclePlusIcon size="2x" />}
                aria-label="Create a new six"
                text="Create a new SIX"
                variant="outline"
                size="lg"
                onClick={handlers.createSix}
              />
)
}
  return (
    <>
      <TwoRowLayout
        top={
          <TwoColumn
            left={
             getNewSixButton()
            }
            // right={<SearchInput placeholder="Search for a SIX" />}
            right={<></>}
            align="center"
            justify="space-between"
            gap="3rem"
          />
        }
        bottom={
          <Table
            data={state.sixes}
            columns={columns}
            onRowClick={handleRowClick}
          />
        }
      />

      <CreateSixModal />
    </>
  );
}

export default SixListing;
